import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

// "Add to Home Screen" banner.
// - Android / desktop Chrome: uses the native `beforeinstallprompt` event.
// - iOS Safari: no such event, so we show a tip to tap Share → Add to Home Screen.
export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("aw_install_dismissed") === "1") return;
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (standalone) return; // already installed

    const ua = window.navigator.userAgent || "";
    const isIOS = /iphone|ipad|ipod/i.test(ua) && !/crios|fxios|edgios/i.test(ua); // iOS Safari only
    if (isIOS) {
      setIos(true);
      const t = setTimeout(() => setVisible(true), 2500); // let the page settle first
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setVisible(true); };
    const onInstalled = () => { setVisible(false); localStorage.setItem("aw_install_dismissed", "1"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (_) {}
    setDeferred(null);
    setVisible(false);
    localStorage.setItem("aw_install_dismissed", "1");
  };

  const dismiss = () => { setVisible(false); localStorage.setItem("aw_install_dismissed", "1"); };

  if (!visible) return null;

  return (
    <div data-testid="install-prompt"
      className="fixed inset-x-3 bottom-3 z-[90] mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-[#00FFC2]/30 bg-black/85 p-3 pr-2 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.9)] backdrop-blur-xl fade-up sm:inset-x-auto sm:right-4">
      <img src="/pwa-192.png" alt="Air Wave" className="h-11 w-11 shrink-0 rounded-xl object-contain" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-white">Install Air Wave</p>
        {ios ? (
          <p className="text-[12px] leading-snug text-white/60">
            Tap <Share size={12} className="mx-0.5 inline -translate-y-0.5 text-[#00FFC2]" /> then <span className="font-semibold text-white/80">“Add to Home Screen”</span>
          </p>
        ) : (
          <p className="truncate text-[12px] text-white/60">Full-screen, ad-free, one tap from your home screen.</p>
        )}
      </div>
      {!ios && (
        <button onClick={install} data-testid="install-accept"
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#00FFC2] px-4 py-2 text-sm font-bold text-black transition-transform hover:scale-105">
          <Download size={15} /> Install
        </button>
      )}
      <button onClick={dismiss} data-testid="install-dismiss" title="Dismiss"
        className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white/50 hover:bg-white/10 hover:text-white">
        <X size={16} />
      </button>
    </div>
  );
};
